// D-0099 — NF por parcela nas telas da equipe (Efetivo e Financeiro).
// `nf_parcelas` vem da API: [{ id, numero, total, valor, data_pagamento, financeiroparcela_id,
// nfs: [{ id, numero }], anexo_manual, pendente }]. Os ids são texto (o id da parcela passa de 2^53).

export const idsPendentes = (parcelas) => (parcelas || []).filter((p) => p.pendente).map((p) => String(p.id));

// Pedido de NF: padrão = as parcelas que ainda estão sem NF.
export const pedidoPadrao = (parcelas) => idsPendentes(parcelas);

// Parcelas marcadas no pedido que já têm NF: o fornecedor manda a nota nova e a antiga continua
// até a equipe removê-la (pedir de novo vale — Rafael, 25/10).
export const marcadasComNf = (parcelas, marcadas) => (parcelas || [])
    .filter((p) => !p.pendente && (marcadas || []).includes(String(p.id)));

export const avisoPedidoDeNovo = (parcelas, marcadas) => {
    const lista = marcadasComNf(parcelas, marcadas);
    if (!lista.length) return '';
    const nums = lista.map((p) => p.numero);
    const quais = nums.length === 1 ? `A parcela ${nums[0]} já tem nota` : `As parcelas ${nums.slice(0, -1).join(', ')} e ${nums[nums.length - 1]} já têm nota`;
    const comNota = lista.filter((p) => (p.nfs || []).length);
    if (!comNota.length) {
        return `${quais}: o fornecedor manda a nota nova pelo link, e o anexo antigo continua até ser tirado pelo Contas a Pagar.`;
    }
    // Nota antiga que também cobre parcela não pedida de novo sai só destas pelo botão Parcelas;
    // senão, pelo Remover (o modal Parcelas não deixa a nota sem parcela nenhuma).
    const pedidas = (marcadas || []).map(String);
    const notas = comNota.reduce((ids, p) => ids.concat(p.nfs.map((n) => String(n.id))), []);
    const cobreOutras = (parcelas || []).some((p) => !pedidas.includes(String(p.id))
        && (p.nfs || []).some((n) => notas.includes(String(n.id))));
    const como = cobreOutras
        ? 'no Efetivo, pelo botão Parcelas da nota (o Remover tiraria a nota das outras parcelas também)'
        : 'no Efetivo, pelo Remover da nota';
    const anexo = lista.some((p) => p.anexo_manual) ? '; anexo do Contas a Pagar sai pelo Contas a Pagar' : '';
    return `${quais}: o fornecedor manda a nota nova pelo link, e a antiga continua até ser tirada ${como}${anexo}.`;
};

// NF subida pela equipe: plano de uma parcela = ela; senão, a primeira pendente.
export const notaPadrao = (parcelas) => {
    const lista = parcelas || [];
    if (lista.length === 1) return [String(lista[0].id)];
    const p = idsPendentes(lista);
    return p.length ? [p[0]] : [];
};

export const alternar = (marcadas, id) => {
    const s = String(id);
    return (marcadas || []).includes(s) ? marcadas.filter((m) => m !== s) : [...(marcadas || []), s];
};

// Ids na ordem do plano (é o que vai para a API).
export const ordenarPeloPlano = (parcelas, marcadas) => (parcelas || [])
    .map((p) => String(p.id))
    .filter((id) => (marcadas || []).includes(id));

export const somaMarcadas = (parcelas, marcadas) => (parcelas || [])
    .filter((p) => (marcadas || []).includes(String(p.id)))
    .reduce((t, p) => t + Math.round(Number(p.valor || 0) * 100), 0) / 100;

export const dataBr = (ymd) => {
    if (!ymd) return '—';
    const [a, m, d] = String(ymd).slice(0, 10).split('-');
    return d && m && a ? `${d}/${m}/${a}` : '—';
};

// O que a parcela mostra no lugar da NF: o número dela, "NF anexada" (Contas a Pagar) ou "NF pendente".
export const situacaoNfParcela = (p) => {
    const nfs = (p && p.nfs) || [];
    if (nfs.length) {
        const nums = nfs.map((n) => n.numero).filter(Boolean);
        // D-0128: parcela da forma Apólice — a nota é a apólice, não uma NF.
        const apolice = nfs.every((n) => n.apolice);
        const texto = apolice
            ? (nums.length ? 'Apólice nº ' + nums.join(' + ') : (nfs.length > 1 ? nfs.length + ' apólices recebidas' : 'Apólice recebida'))
            : (nums.length ? 'NF nº ' + nums.join(' + ') : (nfs.length > 1 ? nfs.length + ' NFs recebidas' : 'NF recebida'));
        return { texto, tom: 'is-ok' };
    }
    if (p && p.anexo_manual) return { texto: 'NF anexada', tom: 'is-ok' };
    return { texto: 'NF pendente', tom: 'is-warn' };
};

// "Parcela 2 de 3" / "Parcelas 1 e 2 de 3" / "Parcelas 1, 2 e 3 de 3" — o que uma NF cobre.
export const rotuloParcelasDaNf = (ids, parcelas) => {
    const lista = (parcelas || []).filter((p) => (ids || []).map(String).includes(String(p.id)));
    if (!lista.length) return 'Sem parcela';
    const nums = lista.map((p) => p.numero);
    const total = lista[0].total;
    if (nums.length === 1) return `Parcela ${nums[0]} de ${total}`;
    return `Parcelas ${nums.slice(0, -1).join(', ')} e ${nums[nums.length - 1]} de ${total}`;
};

// A parcela do Efetivo aponta para a de previsão (previsao_parcela_id); a da previsão é ela mesma.
export const nfParcelaDe = (parcelas, parcela) => {
    const id = String((parcela && (parcela.previsao_parcela_id || parcela.id)) || '');
    return (parcelas || []).find((p) => String(p.id) === id) || null;
};

// Tabela "Nota fiscal de cada parcela" do Efetivo (Rafael 25/10): cada NF da parcela numa linha, com Baixar,
// Status da conferência, Detalhes e Excluir. A parcela traz só { id, numero } das NFs; o resto vem de `forn.nfs`.
export const parcelasComNotas = (parcelas, nfs) => {
    const porId = {};
    (nfs || []).forEach((nf) => { if (nf && nf.id) porId[String(nf.id)] = nf; });
    return (parcelas || []).map((p) => ({
        parcela: p,
        notas: ((p && p.nfs) || []).map((n) => porId[String(n.id)] || n),
    }));
};

// NFs recebidas que não aparecem em nenhuma parcela: seguem no bloco de notas avulsas, embaixo da tabela.
export const notasForaDasParcelas = (parcelas, nfs) => {
    const naTabela = {};
    (parcelas || []).forEach((p) => ((p && p.nfs) || []).forEach((n) => { naTabela[String(n.id)] = true; }));
    return (nfs || []).filter((nf) => !nf || !nf.id || !naTabela[String(nf.id)]);
};

// Status da conferência automática: Aprovado (verde), Divergente (laranja) ou Não analisada (cinza).
// NF sem conferência (anterior ao recurso) conta como não analisada; só a aprovada não pede "analisar de novo".
const STATUS_CONFERENCIA = {
    ok: { texto: 'Aprovado', tom: 'is-ok' },
    divergente: { texto: 'Divergente', tom: 'is-warn' },
    nao_verificado: { texto: 'Não analisada', tom: 'is-muted' },
};
export const statusConferenciaNf = (nf) => {
    const v = nf && nf.validacao;
    const chave = v && STATUS_CONFERENCIA[v.status] ? v.status : 'nao_verificado';
    return Object.assign({}, STATUS_CONFERENCIA[chave], { analisarDeNovo: chave !== 'ok' && !!(nf && nf.id) });
};

// Excluir a NF numa parcela tira a nota de todas as que ela cobre: o aviso diz quais são as outras.
export const avisoExcluirNf = (nf, parcelaId, parcelas) => {
    const outras = ((nf && nf.parcelas) || []).map(String).filter((id) => id !== String(parcelaId));
    if (!outras.length) return '';
    return 'Esta nota também cobre: ' + rotuloParcelasDaNf(outras, parcelas) + '. Ela sai de todas as parcelas.';
};
